import { OPEN_CREDITS_PARAM } from "@pax/api-contract/payments/v1alpha1";
import { createContext, type ReactNode, use, useCallback, useEffect, useState } from "react";
import { useAccountDialog } from "@/features/auth/account-dialog";
import { useSession } from "@/features/auth/queries";
import {
  CheckoutReturnNotice,
  type CheckoutReturnNoticeKind,
} from "./components/CheckoutReturnNotice";
import { TopUpDialog, type TopUpTab } from "./components/TopUpDialog";
import { AUTO_TOP_UP_SUCCESS, useCheckoutBalanceWatch } from "./resume-after-checkout";

interface TopUpContextValue {
  /** Opens buying credits, on the Monthly or One-time tab when one is given. */
  openTopUp: (tab?: TopUpTab) => void;
}

const TopUpContext = createContext<TopUpContextValue | null>(null);

export function useTopUp(): TopUpContextValue {
  const value = use(TopUpContext);
  if (!value) throw new Error("useTopUp must be used inside TopUpProvider");
  return value;
}

/** One picker for every way into buying credits. */
export function TopUpProvider({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const { openAccountDialog } = useAccountDialog();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TopUpTab | undefined>();
  const [notice, setNotice] = useState<CheckoutReturnNoticeKind | null>(null);
  const returned = useCheckoutBalanceWatch();
  const dismissNotice = useCallback(() => setNotice(null), []);

  useEffect(() => {
    if (!returned) return;
    if (returned === "auto_top_up_enabled") setOpen(false);
    setNotice(returned);
  }, [returned]);

  // Emails link to `/?credits` to open the dialog. Someone signed out signs in and comes back to it.
  useEffect(() => {
    if (session === undefined) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has(OPEN_CREDITS_PARAM)) return;
    url.searchParams.delete(OPEN_CREDITS_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
    if (session?.user && !session.user.isAnonymous) setOpen(true);
    else openAccountDialog(undefined, { next: `/?${OPEN_CREDITS_PARAM}` });
  }, [session, openAccountDialog]);

  useEffect(() => {
    const onAutoTopUp = () => {
      setOpen(false);
      setNotice("auto_top_up_enabled");
    };
    window.addEventListener(AUTO_TOP_UP_SUCCESS, onAutoTopUp);
    return () => window.removeEventListener(AUTO_TOP_UP_SUCCESS, onAutoTopUp);
  }, []);

  return (
    <TopUpContext
      value={{
        // A guest has nothing to charge, so topping up starts with an account.
        openTopUp: next => {
          if (session?.user.isAnonymous) return openAccountDialog();
          setTab(next);
          setOpen(true);
        },
      }}
    >
      {children}
      <TopUpDialog open={open} onOpenChange={setOpen} initialTab={tab} />
      {notice && <CheckoutReturnNotice kind={notice} onDismiss={dismissNotice} />}
    </TopUpContext>
  );
}
